import { useEffect, useState } from 'react';

import { mutations } from 'entities';

import { Box, Button, Column, Input, Row } from 'components/Blocks';
import { CheckIcon, MessageCircle } from 'components/Blocks/Icons';

import { useAuth, useUser } from 'providers/Auth';
import { useLang } from 'locale';

import { phoneByCountry } from './utils';
import { ResendButtonCode } from './ResendButton';
import { onlyNumericChars } from 'utils/text';

interface ConfirmWppFormProps {
  phone: string;
  onConfirm: () => void;
  onChangeNumber: () => void;
  rewardPoints: number;
}

const CODE_LENGTH = 6;
const RESEND_SECONDS = 65;

export function ConfirmWppForm({
  phone,
  onConfirm,
  onChangeNumber,
  rewardPoints,
}: ConfirmWppFormProps) {
  const { t } = useLang();
  const { country, activeIncentive } = useUser();
  const { updateUser } = useAuth();

  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(RESEND_SECONDS);

  const phoneConfig = phoneByCountry[country];

  const codeOk = code.length === CODE_LENGTH;

  const confirmWpp = mutations.whatsapp.confirm.use({
    block: !codeOk,
    onSuccess: () => {
      updateUser({
        activeIncentive: activeIncentive + rewardPoints,
      });
      onConfirm();
    },
    params: { code },
  });

  const sendCode = mutations.whatsapp.sendConfirmationCode.use({
    params: {
      phone: onlyNumericChars(`${phoneConfig.code}${phone}`),
    },

    onSuccess: () => setSeconds(RESEND_SECONDS),
  });

  useEffect(() => {
    if (!seconds) return;

    const timer = setInterval(() => {
      setSeconds((s) => {
        const next = Math.max(s - 1, 0);
        if (!next) clearInterval(timer);
        return next;
      });
    }, 1_000);

    return () => clearInterval(timer);
  }, [seconds]);

  const changeNumber = () => {
    setCode('');

    onChangeNumber();
  };

  return (
    <Column gap="6">
      <Box
        className="mx-auto w-fit flex-grow-0 bg-primary p-3 text-primary-foreground"
        border="circle"
      >
        <MessageCircle size={24} />
      </Box>

      <h2 className="text-center text-xl font-semibold text-primary">{t('verify-whatsapp')}</h2>

      <p className="max-w-sm text-center text-success">
        <CheckIcon className="mr-2 inline" />

        <span>{t('code-sent')}</span>
      </p>

      <p className="text-center text-sm font-semibold">
        {phoneConfig.code} {phone}
      </p>

      <Input
        placeholder={t('confirmation-code-placeholder')}
        size="large"
        value={code}
        onValueChange={setCode}
      />

      <Column>
        <Button className="w-full" {...confirmWpp.buttonProps} disabled={!codeOk}>
          <MessageCircle />
          {t('confirm')}
        </Button>

        {seconds ? (
          <p className="text-center text-sm text-muted-foreground">
            {t('resend-or-change-num', { seconds })}
          </p>
        ) : (
          <Row justifyContent="center">
            <ResendButtonCode seconds={seconds} send={sendCode.onClick} />

            <Button
              className="flex-1"
              variant="outlined"
              onClick={changeNumber}
              disabled={!!seconds}
              color="gray"
            >
              {t('change-number')}
              {!!seconds && ` (${seconds})`}
            </Button>
          </Row>
        )}
      </Column>
    </Column>
  );
}
